import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  TableSortLabel,
} from '@mui/material'
import { Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material'
import { Grupo } from '../../types/api'
import dayjs from 'dayjs'

interface GruposTableProps {
  grupos: Grupo[]
  onEdit: (grupo: Grupo) => void
  onDelete: (grupo: Grupo) => void
  loading?: boolean
}

const GruposTable: React.FC<GruposTableProps> = ({ grupos = [], onEdit, onDelete, loading }) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<keyof Grupo | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null)

  const safeGrupos = Array.isArray(grupos) ? grupos : []

  // Filtrar grupos por termo de busca
  const filteredGrupos = useMemo(
    () =>
      safeGrupos.filter(
        (grupo) =>
          grupo.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (grupo.descricao && grupo.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
      ),
    [safeGrupos, searchTerm]
  )

  const sortableColumns: Array<{
    key: keyof Grupo
    label: string
    align?: 'left' | 'right'
  }> = [
    { key: 'id', label: t('common.id'), align: 'right' },
    { key: 'nome', label: t('groups.name') },
    { key: 'descricao', label: t('groups.description') },
    { key: 'enforce_limite_acessos', label: t('groups.enforceLimitShort') },
    { key: 'limite_sessoes_por_usuario', label: t('groups.limitPerUser') },
    { key: 'permissivo_limite_acessos', label: t('groups.permissiveLimitShort') },
    { key: 'status', label: t('common.status', 'Status') },
    { key: 'created_at', label: t('common.created_at') },
  ]

  const sortValue = (grupo: Grupo, field: keyof Grupo) => {
    const value = grupo[field]
    if (value == null) return null

    if (typeof value === 'number') return value
    if (typeof value === 'boolean') return value ? 1 : 0

    if (field === 'created_at' || field === 'updated_at') {
      return dayjs(value as string).valueOf()
    }

    return String(value).toLowerCase()
  }

  const sortedGrupos = useMemo(() => {
    if (!sortField || !sortDirection) {
      return filteredGrupos
    }

    const multiplier = sortDirection === 'asc' ? 1 : -1
    return [...filteredGrupos].sort((a, b) => {
      const aValue = sortValue(a, sortField)
      const bValue = sortValue(b, sortField)

      if (aValue == null && bValue == null) return 0
      if (aValue == null) return -1 * multiplier
      if (bValue == null) return 1 * multiplier

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * multiplier
      }

      return String(aValue).localeCompare(String(bValue), undefined, {
        sensitivity: 'base',
        numeric: true,
      }) * multiplier
    })
  }, [filteredGrupos, sortDirection, sortField])

  // Paginação
  const paginatedGrupos = sortedGrupos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSort = (field: keyof Grupo) => {
    if (sortField !== field) {
      setSortField(field)
      setSortDirection('asc')
      setPage(0)
      return
    }

    if (sortDirection === 'asc') {
      setSortDirection('desc')
    } else if (sortDirection === 'desc') {
      setSortDirection(null)
      setSortField(null)
    } else {
      setSortDirection('asc')
    }
    setPage(0)
  }

  const getSortDirection = (field: keyof Grupo) => {
    if (sortField !== field || sortDirection == null) return 'asc'
    return sortDirection
  }

  if (loading) {
    return (
      <Box p={3}>
        <Typography>{t('common.loading')}</Typography>
      </Box>
    )
  }

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box p={2}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={t('groups.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            setPage(0)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            '& .MuiInputBase-root': { minHeight: 28 },
            '& .MuiOutlinedInput-input': { py: 0.75 },
          }}
        />
      </Box>

      <TableContainer
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {sortableColumns.map((column) => {
                const isActive = sortField === column.key && sortDirection !== null
                return (
                  <TableCell
                    key={column.key}
                    align={column.align ?? 'left'}
                    sortDirection={isActive ? sortDirection ?? 'asc' : false}
                  >
                    <TableSortLabel
                      active={isActive}
                      direction={getSortDirection(column.key)}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                )
              })}
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedGrupos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" py={3}>
                    {searchTerm ? t('groups.noResults') : t('groups.noData')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedGrupos.map((grupo) => {
                const ativo = (grupo.status ?? 'A') === 'A'
                const enforce = grupo.enforce_limite_acessos ?? true
                const permissivo = grupo.permissivo_limite_acessos ?? false
                return (
                  <TableRow key={grupo.id} hover>
                    <TableCell>{grupo.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {grupo.nome}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {grupo.descricao ? (
                        <Typography variant="body2" color="text.secondary">
                          {grupo.descricao}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          {t('common.noDescription')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={enforce ? t('common.yes') : t('common.no')}
                        color={enforce ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {typeof grupo.limite_sessoes_por_usuario === 'number'
                          ? grupo.limite_sessoes_por_usuario
                          : t('groups.limitUnlimited')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={permissivo ? t('common.yes') : t('common.no')}
                        color={permissivo ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ativo ? t('common.active', 'Ativo') : t('common.inactive', 'Inativo')}
                        color={ativo ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {grupo.created_at ? (
                        <Typography variant="body2" color="text.secondary">
                          {dayjs(grupo.created_at).format('DD/MM/YYYY HH:mm')}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => onEdit(grupo)}
                        color="primary"
                        aria-label={t('common.edit')}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onDelete(grupo)}
                        color="warning"
                        aria-label={t('common.deactivate', 'Inativar')}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sortedGrupos.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage={t('common.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
      />
    </Paper>
  )
}

export default GruposTable

